import { createFileRoute } from "@tanstack/react-router";

import { Button } from "@chartvn/ui/button";

import { FacilityChooser } from "../../features/facility-pick/facility-chooser";
import { SummariesList } from "../../features/summaries/components/summaries-list";
import { SummaryFilterBar } from "../../features/summaries/components/summary-filter-bar";
import {
  anyFilterSet,
  mergeFilterChange,
  summariesSearchSchema,
  type SummaryFilters,
} from "../../features/summaries/summary-filters";

/**
 * `RPM-103`'s Summaries screen (`J-489`): the buildings' daily and weekly summaries, prepared by
 * ChartVN and sent by a nurse from her own mail. Filtered and paged as the worklist is (`CR-45` R5,
 * `J-602`).
 *
 * THE ROUTE COMPOSES TWO FEATURES, as `.claude/rules/coding.md` asks: the Building control is
 * `facility-pick`'s and the other five filters and the list are `summaries`'. Every filter lives in
 * the address, so a reload, a bookmark and Back to summaries all return to the same list.
 *
 * OPENING A ROW OPENS THE SUMMARY ON ITS OWN SCREEN, `/summaries/<id>`. Its id, and how many pages
 * were shown, are written into this address first, so Back to summaries returns to the list as she
 * left it, with focus on that row.
 */
function SummariesScreen() {
  const { pages, opened, ...filters } = Route.useSearch();
  const navigate = Route.useNavigate();

  function change(next: SummaryFilters) {
    // REPLACE, NOT PUSH: Back to summaries steps back over the summary to the list as she left it,
    // not through every filter she tried on the way.
    void navigate({
      search: (current) => mergeFilterChange(current, filters, next),
      replace: true,
    });
  }

  async function open(summaryId: string) {
    await navigate({ search: (prev) => ({ ...prev, opened: summaryId }), replace: true });
    await navigate({ to: "/summaries/$summaryId", params: { summaryId } });
  }

  return (
    <div className="flex flex-col gap-4 pt-4">
      <section
        aria-label="Filters"
        data-testid="summaries-filters"
        className="flex flex-col gap-4 rounded-md border p-4"
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-72">
            <FacilityChooser
              // A remount clears the chooser's text when "Every building" empties the filter.
              key={filters.facilityId ?? "every-building"}
              value={filters.facilityId}
              onChange={(facilityId) => {
                change({ ...filters, facilityId });
              }}
            />
          </div>
          {filters.facilityId === undefined ? null : (
            <Button
              type="button"
              variant="outline"
              data-testid="summaries-every-building"
              className="min-h-11"
              onClick={() => {
                change({ ...filters, facilityId: undefined });
              }}
            >
              Every building
            </Button>
          )}
        </div>
        <SummaryFilterBar filters={filters} onChange={change} />
        {anyFilterSet({ ...filters, facilityId: undefined }) ? (
          <div>
            <Button
              type="button"
              variant="outline"
              data-testid="summaries-clear-filters"
              className="min-h-11"
              onClick={() => {
                change({ facilityId: filters.facilityId });
              }}
            >
              Clear every filter
            </Button>
          </div>
        ) : null}
      </section>
      <SummariesList
        filters={filters}
        pages={pages ?? 1}
        onPagesShown={(shown) => {
          void navigate({ search: (prev) => ({ ...prev, pages: shown }), replace: true });
        }}
        openedSummaryId={opened}
        onOpen={(summaryId) => {
          void open(summaryId);
        }}
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/summaries")({
  // THE PAGE HEADER IS THE SHELL'S: it reads this name and moves focus to its one h1 on arrival.
  staticData: { screenName: "Summaries" },
  validateSearch: summariesSearchSchema,
  component: SummariesScreen,
});
